/**
 * Review Schema Component
 * Provides structured data for individual customer reviews
 * Optimized for Google Rich Results with star ratings
 */

import { useEffect } from "react";

interface Review {
  author: string;
  reviewRating: number;
  datePublished: string;
  reviewBody: string;
  location?: string;
}

interface ReviewSchemaProps {
  reviews?: Review[];
  itemReviewed?: {
    type: string;
    name: string;
  };
}

// Sample verified reviews for schema
const defaultReviews: Review[] = [
  {
    author: "Michael T.",
    reviewRating: 5,
    datePublished: "2026-01-15",
    reviewBody: "California Energy Group made going solar incredibly easy. $Zero down, professional installation in 2 days, and my first bill was 65% lower. The battery backup has already saved us during two power outages. Highly recommend!",
    location: "San Diego, CA"
  },
  {
    author: "Jennifer L.",
    reviewRating: 5,
    datePublished: "2025-12-20",
    reviewBody: "After comparing 5 solar companies, CEAG offered the best PPA terms. Their energy advisor Eric was incredibly knowledgeable about NEM 3.0 and helped us understand why battery storage was essential. Our Tesla Powerwall has been flawless.",
    location: "Los Angeles, CA"
  },
  {
    author: "Robert K.",
    reviewRating: 5,
    datePublished: "2025-11-08",
    reviewBody: "Escaping PG&E rate hikes was our main goal. California Energy Group delivered exactly what they promised - $ Zero upfront, lower monthly bills from day one, and peace of mind during outages. The 25-year warranty sealed the deal.",
    location: "Sacramento, CA"
  },
  {
    author: "Maria G.",
    reviewRating: 5,
    datePublished: "2025-10-22",
    reviewBody: "Excellent service from start to finish. The team handled all permits and utility paperwork. Installation was quick and clean. Now saving over $300/month and our Enphase battery keeps essential loads running during PSPS events.",
    location: "Fresno, CA"
  },
  {
    author: "David W.",
    reviewRating: 5,
    datePublished: "2025-09-14",
    reviewBody: "SDG&E rates were killing us - over $500/month. CEAG's PPA cut that nearly in half with no money down. The Franklin battery system works perfectly. Best home investment we've ever made.",
    location: "Escondido, CA"
  }
];

const ReviewSchema = ({
  reviews = defaultReviews,
  itemReviewed = {
    type: "LocalBusiness",
    name: "California Energy Group"
  }
}: ReviewSchemaProps) => {

  useEffect(() => {
    const reviewSchemas = reviews.map((review, index) => ({
      "@context": "https://schema.org",
      "@type": "Review",
      "@id": `https://www.gosolar.pro/#review-${index}`,
      "author": {
        "@type": "Person",
        "name": review.author,
        ...(review.location && {
          "address": {
            "@type": "PostalAddress",
            "addressLocality": review.location.split(",")[0].trim(),
            "addressRegion": "CA"
          }
        })
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.reviewRating.toString(),
        "bestRating": "5",
        "worstRating": "1"
      },
      "datePublished": review.datePublished,
      "reviewBody": review.reviewBody,
      "itemReviewed": {
        "@type": itemReviewed.type,
        "@id": "https://www.gosolar.pro/#organization",
        "name": itemReviewed.name
      },
      "publisher": {
        "@type": "Organization",
        "name": "California Energy Group"
      }
    }));

    const scriptId = "review-schema";
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      document.head.appendChild(script);
    }
    
    script.textContent = JSON.stringify(reviewSchemas);

    return () => {
      const toRemove = document.getElementById(scriptId);
      if (toRemove) toRemove.remove();
    };
  }, [reviews, itemReviewed]);

  return null;
};

// Pre-configured review schemas for different contexts
export const CompanyReviewsSchema = () => (
  <ReviewSchema
    reviews={defaultReviews}
    itemReviewed={{
      type: "LocalBusiness",
      name: "California Energy Group"
    }}
  />
);

export const PPAReviewsSchema = () => (
  <ReviewSchema
    reviews={defaultReviews.filter(r => r.reviewBody.toLowerCase().includes("ppa"))}
    itemReviewed={{
      type: "Product",
      name: "$ Zero Down Solar PPA"
    }}
  />
);

export default ReviewSchema;
